import { ACTIONS, can, canSee } from "./access";
import {
  RoleProfile,
  isClientUser,
  isDeveloper,
  isInternalLike,
  isManager,
  isPartnershipTeam,
  isPlatformAdmin,
  isRegularInternalUser,
  isSuperAdmin,
} from "./legacyRoles";

export type { RoleProfile };
export {
  isClientUser,
  isDeveloper,
  isInternalLike,
  isManager,
  isPartnershipTeam,
  isPlatformAdmin,
  isRegularInternalUser,
  isSuperAdmin,
};

export const canSeeDataStorage = (p: RoleProfile): boolean =>
  canSee(p, "data_storage");

export const canSeeIntegrations = (p: RoleProfile): boolean =>
  canSee(p, "integrations");

export const canSeeAi = (p: RoleProfile): boolean =>
  can(p, ACTIONS.AI_AGENT_OPEN);

export const canSeeSecureExchange = (p: RoleProfile): boolean =>
  canSee(p, "secure_exchange");

export const canSeePartnerDirectory = (p: RoleProfile): boolean =>
  canSee(p, "partner_directory");

export const canSeeUserManagement = (p: RoleProfile): boolean =>
  canSee(p, "user_management");

export const canSeeSftpManagement = (p: RoleProfile): boolean =>
  canSee(p, "sftp_management");

export const canSeeBotsManagement = (p: RoleProfile): boolean =>
  canSee(p, "bots_management");

// Bots Management has no read-only tier: whoever may open it may operate it,
// and only Client View makes it read-only.
export const isBotsOperator = canSeeBotsManagement;

export const canSeePartnerManagement = (p: RoleProfile): boolean =>
  canSee(p, "partner_management");

export const canSeeIntegrationsPlatform = (p: RoleProfile): boolean =>
  canSee(p, "integrations_platform");

// A client user whose "Integrations" is the Integration Platform instead of
// the legacy dashboard (CAL-5774): the Integration Dashboard role plus the
// platform experience, as the server decides it (integrations_platform.portal).
export const isClientPortalUser = (p: RoleProfile): boolean =>
  p?.user_type === "client" && can(p, ACTIONS.INTEGRATIONS_PLATFORM_PORTAL);

// Who may open /integrations-beta*: every internal platform role, plus client
// portal users, who get the client-facing variant of those pages.
export const canOpenIntegrationsPlatform = (p: RoleProfile): boolean =>
  canSeeIntegrationsPlatform(p) || isClientPortalUser(p);

// Who may open the legacy /integrations* dashboard: anyone with the
// Integrations role whose experience is not the platform.
export const canOpenLegacyIntegrations = (p: RoleProfile): boolean =>
  canSeeIntegrations(p) && !isClientPortalUser(p);

// Where the sidebar "Integrations" entry and the Home tile lead.
export const integrationsHomePath = (p: RoleProfile): string =>
  isClientPortalUser(p) ? "/integrations-beta" : "/integrations";

export const canSeeAnyManagement = (p: RoleProfile): boolean =>
  canSeeUserManagement(p) ||
  canSeeSftpManagement(p) ||
  canSeePartnerManagement(p) ||
  canSeeIntegrationsPlatform(p) ||
  canSeeBotsManagement(p);

export function roleLabel(p: RoleProfile): string {
  if (!p) return "";
  if (p.is_archived) return "Archived";
  if (p.access?.group) return p.access.group.name;
  if (p.user_type === "internal") return "No access group";
  return "Client User";
}
